<template>
  <div class="lp-google-map" :id="`lp-map-${inputId}`" aria-hidden="true"></div>
</template>

<script lang="ts">
  import { Loader } from '@googlemaps/js-api-loader';
  import { v4 as uuid } from 'uuid';
  import { ICenter, ILocationMarker } from "../helpers/LocationFinderTypes"
  import { onMounted, reactive, ref, watch, defineEmits } from 'vue';

  export default {
    name: 'GoogleMap',
    props: {
      apiKey: {
        type: String,
        required: true,
      },
      center: {
        type: Object,
        required: true,
      },
      initialMarker: {
        type: Boolean,
        required: false,
        default: true,
      },
      markers: {
        type: Array,
        required: false,
        default: () => [],
      },
      zoom: {
        type: Number,
        required: false,
        default: 17,
        validator: (value) => {
          return value >= 0 && value <= 21;
        },
      },
      fitMapToLocations: {
        type: Boolean,
        required: false,
        default: false
      },
      mapBounds: {
        type: Object,
        required: false,
        default: null
      }
    },
    setup(props, { emit }) {
      const inputId = ref(uuid());

      // MAP
      let map: google.maps.Map;

      const loader = new Loader({
        apiKey: props.apiKey,
        version: 'weekly',
        libraries: ['places'],
      });

      const mapOptions = reactive({
        center: props.center,
        disableDefaultUI: true,
        zoom: props.zoom,
        zoomControl: true,
        scaleControl: true
      });

      const initMap = () => {
        loader
          .load()
          .then(() => {
            return new google.maps.Map(
              document.getElementById(`lp-map-${inputId.value}`),
              mapOptions
            );
          })
          .then((gmap) => {
            map = gmap;

            map.addListener("center_changed", function () {
              const center: ICenter = map.getCenter().toJSON()
              emit("new-center", center)
            })

            initInfoWindow();

            if (props.initialMarker) {
              createInitialMarker({ position: props.center });
            }

            if (props.markers.length > 0) {
              updateMarkers();
            }

          })
          .catch((e) => {
            // do something
            console.error(e);
          });
      };

      const fitMapToMarkers = () => {
        if (map) {
          if (liveMarkers.length > 0) {
            const mapBounds = new google.maps.LatLngBounds();
            for (var i = 0; i < liveMarkers.length; i++) {
              mapBounds.extend(liveMarkers[i].position);
            }

            map.fitBounds(mapBounds);

            if (map.getZoom() > props.zoom) {
              map.setZoom(props.zoom);
            }
          }
        }
      };

      const moveToLocation = () => {
        if (map) {
          const center = new google.maps.LatLng(props.center);
          map.panTo(center);
        }
      };

      watch(
        () => props.center,
        (newVal, oldVal) => {
          if (newVal.lat !== oldVal.lat || newVal.lng !== oldVal.lng) {
            moveToLocation();
          }
        }
      );

      // MARKERS
      let liveMarkers = [];
      let infoWindow: google.maps.InfoWindow;

      const createInitialMarker = (marker) => {
        const newMarker = new google.maps.Marker({
          position: marker.position,
          optimized: false,
          map,
        });
        liveMarkers.push(newMarker);
      };

      const createMarker = (marker: ILocationMarker) => {
        const markerIcon = createMarkerIcon(marker.PinBackground, marker.PinBorder, marker.Base64PinIcon);

        const newMarker = new google.maps.Marker({
          position: { lat: marker.Latitude as number, lng: marker.Longitude as number },
          title: marker.Title ? marker.Title : '',
          optimized: false,
          map,
          icon: markerIcon
        });

        newMarker.set("id", marker.Id);

        newMarker.addListener('click', () => openInfoWindow(newMarker, marker));
        liveMarkers.push(newMarker);
      };

      const updateMarkers = () => {

        const liveMarkersIds: string[] = liveMarkers.map(elm => elm.id);
        const newMarkersIds: string[] = props.markers.map(elm => elm.Id);

        const markersIdToRemove = liveMarkersIds.filter(elm => !newMarkersIds.some(item => item == elm));
        const markersIdToAppend = newMarkersIds.filter(elm => !liveMarkersIds.some(item => item == elm));

        liveMarkers.forEach((marker, index) => {
          if (markersIdToRemove.indexOf(marker.id) != -1) {
            marker.setMap(null);
          }
        });

        (props.markers as ILocationMarker[]).forEach((marker, index) => {
          if (marker.Latitude && marker.Longitude && markersIdToAppend.some(item => item == marker.Id)) {
            createMarker(marker);
          }
        });

        liveMarkers = liveMarkers.filter(elm => !markersIdToRemove.some(item => item == elm.id));

        fitMapToMarkers();
      };

      //Here we're creating the icon dinamically, with a custom color and border for each location.
      //In order to add an incon on top, we should add an <image> tag and pass the icon on BASE64 using the same snippet data:image/png+xml;utf-8, <ICON>
      const createMarkerIcon = (pinColor, borderColor, pinIcon) => {
        return {
          anchor: new google.maps.Point(27, 54),
          url: 'data:image/svg+xml;utf-8, \
                        <svg width="32" height="32" viewBox="0 0 293 423" fill="'+ encodeURIComponent(pinColor) + '" xmlns="http://www.w3.org/2000/svg"> \
                            <path d="M293 146.482C293 227.382 146.5 423 146.5 423C146.5 423 0 227.382 0 146.482C0 65.5824 65.5903 0 146.5 0C227.41 0 293 65.5824 293 146.482Z" stroke="'+ encodeURIComponent(borderColor) + '" stroke-width="15" fill="' + encodeURIComponent(pinColor) + '"/> \
                            <image href="'+ pinIcon + '" width="200" height="200" x="47" y="47" /> \
                        </svg>',
          scaledSize: new google.maps.Size(54, 54)
        }
      };

      const createInfoContent = (info: ILocationMarker) => {
        return `
                     <div id="content" class="lp-map-info-window">
                        <div id="siteNotice"></div>
                        <h4 class="text-primary">${info.DisplayName || ""}</h4>
                        ${info.Details ? `<div> ${info.Details} </div>` : ``}
                        <br />
                        ${info.Hours ? `<div> ${info.Hours} </div>` : ``}
                        <div id="bodyContent">
                              <p>Address: <br/>
                                  <a class="d-inline" href="https://maps.google.com/?q= ${info.Street || ""} ${info.City || ""} ${info.State || ""}" target="_blank" rel="noopener">
                                    ${info.Street || ""}, <br/> ${info.City || ""}, ${info.State || ""} ${info.Zip || ""}
                                  </a>
                              </p>
                            ${info.Hours_open_value ? `<p>${info.Hours_open_value}</p>` : ``}
                            ${info.Phone ? `<p>Phone: </br> <a href="tel:${info.Phone.replace(/[^0-9]/g, '')}">${info.Phone}</a> </p>` : ``}
                            ${info.Fax ? `<p>Fax: </br> <a href="tel:${info.Fax.replace(/[^0-9]/g, '')}">${info.Fax}</a></p>` : ``}
                            ${info.Url ? `<p>Url: </br> <a href="${info.Url}">View Details</a></p>` : ``}
                        </div>
                    </div>`;
      };

      const initInfoWindow = () => {
        infoWindow = new google.maps.InfoWindow({ content: 'wat' });
      };

      const openInfoWindow = (marker, info) => {
        if (infoWindow) {
          infoWindow.setContent(createInfoContent(info));
          infoWindow.open(map, marker);
        }
      };

      watch(
        () => props.markers,
        () => updateMarkers()
      );

      //watch(props.mapBounds, (bounds: google.maps.LatLngBounds) => fitMapToMarkers(bounds));

      onMounted(() => {
        initMap();
      });

      return { inputId };
    },
  };
</script>

<style lang="scss" scoped></style>
